import { Link } from 'react-router-dom';

import { motion } from 'framer-motion';

import { slideUpVariants, socialIconVariants, staggerContainerVariants } from '@/animations';
import { LazyImage } from '@/components';
import { FOOTER_CONTENT, NAV_CONTENT } from '@/content';
import { Icon, type IconName } from '@/utils/icons';

const Footer = () => {
  const { brand, quickLinks, shopLinks, helpLinks, social, contact, legalLinks, copyright } =
    FOOTER_CONTENT;

  return (
    <motion.footer
      initial="initial"
      whileInView="animate"
      viewport={{ once: true, margin: '-100px' }}
      className="relative mt-8 overflow-hidden border-t border-neutral-100 bg-white pt-8 pb-4 dark:border-neutral-800 dark:bg-neutral-950">
      <div className="from-primary-100/40 pointer-events-none absolute top-0 left-0 z-0 h-96 w-full bg-gradient-to-b to-transparent" />

      <div className="container-toys relative z-10">
        <motion.div
          variants={staggerContainerVariants}
          className="mb-12 grid grid-cols-1 items-start gap-12 lg:grid-cols-12 lg:gap-8">
          <motion.div variants={slideUpVariants} className="lg:col-span-4">
            <Link to="/" className="group mb-8 flex items-center gap-4">
              <motion.div
                whileHover={{ rotate: -10, scale: 1.1 }}
                className="relative h-14 w-14 items-center justify-center transition-shadow">
                <LazyImage
                  src={NAV_CONTENT.logo.src}
                  alt={brand.name}
                  className="h-full w-full"
                  imgClassName="object-contain!"
                />
              </motion.div>
              <div className="flex flex-col">
                <span className="font-display group-hover:text-primary-600 text-2xl font-black tracking-tight text-neutral-900 transition-colors">
                  {brand.name}
                </span>
                <span className="text-primary-500 text-xs font-black tracking-widest uppercase">
                  {brand.tagline}
                </span>
              </div>
            </Link>
            <p className="font-body mb-8 text-base leading-relaxed text-neutral-500">
              {brand.description}
            </p>
            <div className="flex items-center gap-3">
              {social.platforms.map(platform => (
                <motion.a
                  key={platform.id}
                  href={platform.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover="hover"
                  whileTap="tap"
                  variants={socialIconVariants}
                  className="hover:bg-primary-50 hover:text-primary-600 dark:hover:text-primary-400 flex h-10 w-10 items-center justify-center rounded-2xl bg-white text-neutral-500 shadow-sm ring-1 ring-neutral-100 transition-all dark:bg-neutral-900 dark:text-neutral-500 dark:ring-neutral-800 dark:hover:bg-neutral-800"
                  aria-label={platform.id}>
                  <Icon name={platform.icon as IconName} size={20} />
                </motion.a>
              ))}
            </div>
          </motion.div>

          <motion.div
            variants={staggerContainerVariants}
            className="grid grid-cols-2 gap-8 md:grid-cols-4 lg:col-span-8">
            {[quickLinks, shopLinks, helpLinks].map(section => {
              const headingId = `footer-heading-${section.title.toLowerCase().replace(/\s+/g, '-')}`;
              return (
                <motion.div key={section.title} variants={slideUpVariants}>
                  <nav aria-labelledby={headingId}>
                    <h2
                      id={headingId}
                      className="font-display mb-6 text-sm font-black tracking-wider text-neutral-900 uppercase dark:text-neutral-200">
                      {section.title}
                    </h2>
                    <ul className="space-y-4">
                      {section.links.map(link => {
                        const parsePathAndState = (path: string) => {
                          if (path.includes('?')) {
                            const [pathname, search] = path.split('?');
                            const params = new URLSearchParams(search);
                            const state: Record<string, any> = {};

                            const category = params.get('category');
                            if (category) state.category = category;

                            const sort = params.get('sort');
                            if (sort) state.sortBy = sort;

                            const sale = params.get('sale');
                            if (sale) state.isSale = sale === 'true';

                            const q = params.get('q');
                            if (q) state.searchQuery = q;

                            return { to: pathname, state };
                          }
                          return { to: path };
                        };
                        const parsed = parsePathAndState(link.path);
                        return (
                          <li key={link.label}>
                            <Link
                              to={parsed.to}
                              state={parsed.state}
                              className="font-body group hover:text-primary-600 dark:hover:text-primary-400 flex items-center text-sm font-bold text-neutral-500 transition-all dark:text-neutral-400">
                              <span className="bg-primary-400 h-1.5 w-0 rounded-full transition-all group-hover:mr-2 group-hover:w-1.5" />
                              {link.label}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </nav>
                </motion.div>
              );
            })}

            <motion.div variants={slideUpVariants}>
              {' '}
              <h2 className="font-display mb-6 text-sm font-black tracking-wider text-neutral-900 uppercase dark:text-neutral-200">
                {contact.title}
              </h2>
              <ul className="space-y-5">
                <li className="flex flex-col gap-1">
                  <span className="text-xs font-black tracking-widest text-neutral-400 uppercase">
                    {contact.emailLabel}
                  </span>
                  <a
                    href={`mailto:${contact.email}`}
                    className="hover:text-primary-600 text-sm font-bold text-neutral-700 transition-colors dark:text-neutral-300">
                    {contact.email}
                  </a>
                </li>
                <li className="flex flex-col gap-1">
                  <span className="text-xs font-black tracking-widest text-neutral-400 uppercase">
                    {contact.phoneLabel}
                  </span>
                  <a
                    href={`tel:${contact.phone.replace(/\D/g, '')}`}
                    className="hover:text-primary-600 text-sm font-bold text-neutral-700 transition-colors dark:text-neutral-300">
                    {contact.phone}
                  </a>
                </li>
                <li className="flex flex-col gap-1">
                  <span className="text-xs font-black tracking-widest text-neutral-400 uppercase">
                    {contact.addressLabel}
                  </span>
                  <span className="text-sm leading-snug font-bold text-neutral-700 dark:text-neutral-300">
                    {contact.address}
                  </span>
                </li>
              </ul>
            </motion.div>
          </motion.div>
        </motion.div>

        <motion.div
          variants={slideUpVariants}
          className="flex flex-col items-center justify-between gap-6 border-t border-neutral-100 pt-4 md:flex-row dark:border-neutral-800">
          <p className="font-body order-2 text-xs font-bold text-neutral-400 md:order-1">
            {copyright.text}
          </p>
          <div className="order-1 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 md:order-2">
            {legalLinks.links.map(link => (
              <Link
                key={link.id}
                to={link.path}
                className="hover:text-primary-600 dark:hover:text-primary-400 text-xs font-bold text-neutral-400 transition-colors dark:text-neutral-500">
                {link.label}
              </Link>
            ))}
          </div>
        </motion.div>
      </div>
    </motion.footer>
  );
};

export default Footer;
